import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { Plus, Search, Edit, Trash2, RefreshCw, Filter, Layers, ChevronDown, ChevronRight, ArrowUp, ArrowDown, FolderOpen, Upload, FileText, LogIn, MessageCircle, Send, History, KeyRound } from 'lucide-react';
import { driverService } from '../services/driverService';
import { vehicleService } from '../services/vehicleService';
import { trailerService } from '../services/trailerService';
import { uiMetadataService } from '../services/uiMetadataService';
import { documentService } from '../services/documentService';
import { staticDefinitionService } from '../services/staticDefinitionService';
import { auditService } from '../services/auditService';
import { toast } from 'sonner';
import { useAppAlert } from '../components/AppAlertProvider';
import { exportRowsToExcel } from '../utils/excelExport';
import { useAuth } from '../contexts/AuthContext';
import { ApiError } from '../services/apiService';
import { authService } from '../services/authService';

const REQUIRED_DRIVER_FIELDS = [
  { key: 'name', label: 'Ad Soyad' },
  { key: 'phone', label: 'Telefon' },
  { key: 'licenseNumber', label: 'Ehliyet Numarası' },
  { key: 'licenseType', label: 'Ehliyet Sınıfı' },
  { key: 'joinDate', label: 'İşe Başlama Tarihi' },
] as const;

export function Drivers() {
  const { showConfirm } = useAppAlert();
  const { user: currentUser, impersonateUser } = useAuth();
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncingWorkiom, setSyncingWorkiom] = useState(false);
  const [syncingVehicleInfo, setSyncingVehicleInfo] = useState(false);
  const [syncingSeyir, setSyncingSeyir] = useState(false);
  const [fillingResidenceCoordinates, setFillingResidenceCoordinates] = useState(false);
  const [lastLoginFilter, setLastLoginFilter] = useState<'all' | 'has' | 'none'>('all');
  const [employmentTab, setEmploymentTab] = useState<'active' | 'terminated'>('active');
  const [metadataFields, setMetadataFields] = useState<Array<{ key: string; label: string }>>([]);
  const [metadataColumns, setMetadataColumns] = useState<Array<{ key: string; label: string; visibleByDefault?: boolean }>>([]);
  const [personnelDocumentTypes, setPersonnelDocumentTypes] = useState<string[]>([]);
  const [trailerTypeOptions, setTrailerTypeOptions] = useState<string[]>([]);
  const [personnelDocumentCounts, setPersonnelDocumentCounts] = useState<Record<string, number>>({});
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [showGrouping, setShowGrouping] = useState(false);
  const [groupBy, setGroupBy] = useState<string>('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);
  const [showMobileTools, setShowMobileTools] = useState(false);
  const [vehicleOptions, setVehicleOptions] = useState<any[]>([]);
  const [trailerOptions, setTrailerOptions] = useState<any[]>([]);
  const [personnelModalDriver, setPersonnelModalDriver] = useState<any | null>(null);
  const [personnelDocuments, setPersonnelDocuments] = useState<any[]>([]);
  const [personnelDocumentsLoading, setPersonnelDocumentsLoading] = useState(false);
  const [personnelDocumentUploading, setPersonnelDocumentUploading] = useState(false);
  const [personnelDocumentForm, setPersonnelDocumentForm] = useState({
    documentType: '',
    documentDate: '',
    notes: '',
  });
  const [selectedDriverIds, setSelectedDriverIds] = useState<string[]>([]);
  const [bulkMessageModalOpen, setBulkMessageModalOpen] = useState(false);
  const [bulkMessageText, setBulkMessageText] = useState('');
  const [bulkMessageChannel, setBulkMessageChannel] = useState<'sms' | 'whatsapp'>('sms');
  const [bulkMessageSending, setBulkMessageSending] = useState(false);
  const [modalTab, setModalTab] = useState<'form' | 'kunya'>('form');
  const [driverAuditLogs, setDriverAuditLogs] = useState<any[]>([]);
  const [driverAuditLogsLoading, setDriverAuditLogsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    tcNumber: '',
    cekiciPlaka: '',
    dorsePlaka: '',
    vehicleType: '',
    invoiceTitle: '',
    city: '',
    residenceLatitude: '',
    residenceLongitude: '',
    licenseNumber: '',
    licenseType: '',
    status: 'available' as const,
    srcCardNumber: '',
    srcExpiryDate: '',
    joinDate: '',
    terminationDate: '',
    isControlResponsible: false,
    harcirahTuru: 'sabit',
    workDuration: '',
    currentWeekDrivingHours: 0,
    twoWeekDrivingHours: 0,
  });

  const normalizeDriverName = (value: string) => value.toLocaleUpperCase('tr-TR');

  const formatLogDateTime = (value?: string | null) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const loadDriverAuditLogs = async (driverId: string | number) => {
    setDriverAuditLogsLoading(true);
    try {
      const response: any = await auditService.getByDriver(driverId, 200);
      const logs = Array.isArray(response?.data) ? response.data : [];
      setDriverAuditLogs(
        [...logs].sort(
          (a, b) => new Date(b.logged_at || 0).getTime() - new Date(a.logged_at || 0).getTime()
        )
      );
    } catch (error) {
      console.error('Error loading driver audit logs:', error);
      setDriverAuditLogs([]);
      toast.error('İşlem logları alınamadı');
    } finally {
      setDriverAuditLogsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadMetadata();
    loadTableMetadata();
    loadFleetOptions();
    loadPersonnelDocumentTypes();
    loadTrailerTypeOptions();
  }, []);

  const resolveFileUrl = (rawPath: string) => {
    if (!rawPath) return '';
    if (/^https?:\/\//i.test(rawPath)) return rawPath;
    const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
    return `${baseUrl}${rawPath.startsWith('/') ? '' : '/'}${rawPath}`;
  };

  const loadFleetOptions = async () => {
    try {
      const [vehiclesRes, trailersRes] = await Promise.all([
        vehicleService.getVehicles(),
        trailerService.getTrailers(),
      ]);
      setVehicleOptions(Array.isArray((vehiclesRes as any)?.data) ? (vehiclesRes as any).data : []);
      setTrailerOptions(Array.isArray((trailersRes as any)?.data) ? (trailersRes as any).data : []);
    } catch (error) {
      console.error('Error loading fleet options:', error);
    }
  };

  const loadMetadata = async () => {
    try {
      const response: any = await uiMetadataService.getModuleForm('driver');
      const tabs = Array.isArray(response?.data?.tabs) ? response.data.tabs : [];
      const mainTab = tabs.find((tab: any) => tab.key === 'main') || tabs[0];
      const fields = Array.isArray(mainTab?.fields) ? mainTab.fields : [];
      setMetadataFields(fields);
    } catch (error) {
      console.error('Failed to load driver metadata:', error);
    }
  };

  const loadTableMetadata = async () => {
    try {
      const response: any = await uiMetadataService.getModuleTable('driver');
      const columns = Array.isArray(response?.data?.columns) ? response.data.columns : [];
      setMetadataColumns(columns);
    } catch (error) {
      console.error('Failed to load driver table metadata:', error);
    }
  };

  const loadPersonnelDocumentTypes = async () => {
    try {
      const response: any = await staticDefinitionService.getByKey('internal_driver_personnel_document_types');
      setPersonnelDocumentTypes(Array.isArray(response?.data?.items) ? response.data.items.map((item: any) => String(item.value || '').trim()).filter(Boolean) : []);
    } catch (error) {
      console.error('Failed to load personnel document types:', error);
      setPersonnelDocumentTypes(['Tutanak', 'Sözleşme', 'Savunma', 'Sağlık Raporu', 'Ehliyet Fotokopisi', 'SRC Belgesi', 'Psikoteknik', 'Diğer']);
    }
  };

  const loadTrailerTypeOptions = async () => {
    try {
      const response: any = await staticDefinitionService.getByKey('shipment_vehicle_type');
      setTrailerTypeOptions(
        Array.isArray(response?.data?.items)
          ? response.data.items.map((item: any) => String(item.value || '').trim()).filter(Boolean)
          : []
      );
    } catch (error) {
      console.error('Failed to load trailer type options:', error);
      setTrailerTypeOptions([]);
    }
  };

  const loadPersonnelDocumentCounts = async () => {
    try {
      const response: any = await documentService.getDocuments({ entityType: 'driver_personnel' });
      const items = Array.isArray(response?.data) ? response.data : [];
      const counts = items.reduce((acc: Record<string, number>, item: any) => {
        const key = String(item.entity_id || item.entityId || '');
        if (!key) return acc;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      setPersonnelDocumentCounts(counts);
    } catch (error) {
      console.error('Failed to load personnel document counts:', error);
      setPersonnelDocumentCounts({});
    }
  };

  const loadPersonnelDocuments = async (driverId: string | number) => {
    try {
      setPersonnelDocumentsLoading(true);
      const response: any = await documentService.getDocuments({ entityType: 'driver_personnel', entityId: String(driverId) });
      setPersonnelDocuments(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load personnel documents:', error);
      toast.error('Özlük dosyaları alınamadı');
      setPersonnelDocuments([]);
    } finally {
      setPersonnelDocumentsLoading(false);
    }
  };

  const openPersonnelModal = async (driver: any) => {
    setPersonnelModalDriver(driver);
    setPersonnelDocumentForm({ documentType: personnelDocumentTypes[0] || '', documentDate: '', notes: '' });
    await loadPersonnelDocuments(driver.id);
  };

  const closePersonnelModal = () => {
    setPersonnelModalDriver(null);
    setPersonnelDocuments([]);
    setPersonnelDocumentForm({ documentType: personnelDocumentTypes[0] || '', documentDate: '', notes: '' });
  };

  const handleUploadPersonnelDocument = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !personnelModalDriver) return;
    if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
      toast.error('Sadece PDF dosyası yüklenebilir');
      event.target.value = '';
      return;
    }
    if (!personnelDocumentForm.documentType) {
      toast.error('Evrak türü seçin');
      event.target.value = '';
      return;
    }
    try {
      setPersonnelDocumentUploading(true);
      const form = new FormData();
      form.append('file', file);
      form.append('entityType', 'driver_personnel');
      form.append('entityId', String(personnelModalDriver.id));
      form.append('documentType', personnelDocumentForm.documentType);
      form.append('name', file.name);
      if (personnelDocumentForm.documentDate) form.append('documentDate', personnelDocumentForm.documentDate);
      if (personnelDocumentForm.notes) form.append('notes', personnelDocumentForm.notes);
      await documentService.uploadDocument(form);
      toast.success('Özlük dosyası yüklendi');
      setPersonnelDocumentForm((current) => ({ ...current, documentDate: '', notes: '' }));
      await loadPersonnelDocuments(personnelModalDriver.id);
    } catch (error) {
      console.error('Personnel document upload failed:', error);
      toast.error('Özlük dosyası yüklenemedi');
    } finally {
      setPersonnelDocumentUploading(false);
      event.target.value = '';
    }
  };

  const handleDeletePersonnelDocument = async (documentId: string | number) => {
    if (!personnelModalDriver) return;
    try {
      await documentService.deleteDocument(documentId);
      toast.success('Özlük dosyası silindi');
      await loadPersonnelDocuments(personnelModalDriver.id);
    } catch (error) {
      console.error('Personnel document delete failed:', error);
      toast.error('Özlük dosyası silinemedi');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const response: any = await driverService.getDrivers({ type: 'internal' });
      if (response.success) {
        const mappedDrivers = (response.data || []).map((driver: any) => ({
          ...driver,
          fullName: driver.full_name || driver.fullName || driver.name || '',
          name: driver.full_name || driver.fullName || driver.name || '',
          phone: driver.phone || '',
          licenseNumber: driver.license_number || driver.licenseNumber || '',
          licenseClass: driver.license_class || driver.licenseClass || '',
          srcCardNumber: driver.src_card_number || driver.srcCardNumber || '',
          srcExpiryDate: String(driver.src_expiry_date_text || driver.src_expiry_date || driver.srcExpiryDate || '').split('T')[0],
          joinDate: String(driver.start_date_text || driver.start_date || driver.joinDate || '').split('T')[0],
          terminationDate: String(driver.termination_date_text || driver.termination_date || driver.terminationDate || '').split('T')[0],
          isControlResponsible: Number(driver.is_control_responsible || driver.isControlResponsible || 0) === 1,
          harcirahTuru: driver.harcirah_turu || driver.harcirahTuru || 'sabit',
          workDuration: driver.work_duration || driver.workDuration || '',
          currentWeekDrivingHours: driver.current_week_hours || driver.currentWeekDrivingHours || 0,
          twoWeekDrivingHours: driver.two_week_hours || driver.twoWeekDrivingHours || 0,
          status: driver.status || 'available',
          userStatus: driver.user_status || driver.userStatus || '',
          lastLogin: driver.last_login_at_text || '',
          lastLoginPlatform: driver.last_login_platform || '',
          cekiciPlaka: driver.assigned_vehicle_plate || driver.cekici_plaka || driver.cekiciPlaka || '',
          dorsePlaka: driver.assigned_trailer_plate || driver.dorse_plaka || driver.dorsePlaka || '',
          assignedVehicleId: driver.assigned_vehicle_id ? String(driver.assigned_vehicle_id) : '',
          assignedVehiclePlate: driver.assigned_vehicle_plate || '',
          assignedTrailerPlate: driver.assigned_trailer_plate || '',
          assignedVehicleBrand: driver.assigned_vehicle_brand || '',
          assignedVehicleModel: driver.assigned_vehicle_model || '',
          tcNumber: driver.tc_number || '',
          invoiceTitle: driver.invoice_title || '',
          city: driver.city || '',
          residenceLatitude: driver.residence_latitude || '',
          residenceLongitude: driver.residence_longitude || '',
          vehicleType: driver.assigned_vehicle_type || driver.vehicle_type || '',

        }));
        setDrivers(mappedDrivers);
        await loadPersonnelDocumentCounts();
      } else {
        toast.error(response.message || 'Veri yüklenirken hata oluştu');
      }
    } catch (error) {
      console.error('Error loading drivers:', error);
      toast.error('Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const missingFields = REQUIRED_DRIVER_FIELDS
      .filter(({ key }) => !String(formData[key] ?? '').trim())
      .map(({ label }) => label);

    if (missingFields.length > 0) {
      toast.error(`Zorunlu alanlar eksik: ${missingFields.join(', ')}`);
      return;
    }
     
    try {
      const driverData = {
        full_name: normalizeDriverName(formData.name),
        phone: formData.phone,
        email: formData.email || editingDriver?.email || null,
        tc_number: formData.tcNumber || null,
        cekici_plaka: formData.cekiciPlaka || null,
        dorse_plaka: formData.dorsePlaka || null,
        vehicle_type: formData.vehicleType || null,
        invoice_title: formData.invoiceTitle || null,
        city: formData.city || null,
        residence_latitude: formData.residenceLatitude || null,
        residence_longitude: formData.residenceLongitude || null,
        license_number: formData.licenseNumber,
        license_class: formData.licenseType,
        status: formData.status,
        src_card_number: formData.srcCardNumber || null,
        src_expiry_date: formData.srcExpiryDate || null,
        start_date: formData.joinDate || null,
        termination_date: formData.terminationDate || null,
        is_control_responsible: formData.isControlResponsible ? 1 : 0,
        harcirah_turu: formData.harcirahTuru,
        work_duration: formData.workDuration || null,
        current_week_hours: formData.currentWeekDrivingHours === '' ? null : formData.currentWeekDrivingHours,
        two_week_hours: formData.twoWeekDrivingHours === '' ? null : formData.twoWeekDrivingHours,
      };

      if (editingDriver) {
        const response = await driverService.updateDriver(editingDriver.id, driverData);
        if (response.success) {
          toast.success('Sürücü güncellendi');
        } else {
          toast.error(response.message || 'Güncelleme başarısız');
        }
      } else {
        const response = await driverService.createDriver(driverData);
        if (response.success) {
          toast.success('Sürücü oluşturuldu');
        } else {
          toast.error(response.message || 'Oluşturma başarısız');
        }
      }

      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving driver:', error);
      const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'Sürücü kaydedilemedi';
      toast.error(message);
    }
  };

  const handleEdit = (driver: any) => {
    setEditingDriver(driver);
    setFormData({
      name: normalizeDriverName(driver.fullName || ''),
      email: driver.email || '',
      phone: driver.phone || '',
      tcNumber: driver.tcNumber || '',
        cekiciPlaka: driver.assignedVehiclePlate || driver.cekiciPlaka || '',
        dorsePlaka: driver.assignedTrailerPlate || driver.dorsePlaka || '',
      vehicleType: driver.vehicleType || '',
      invoiceTitle: driver.invoiceTitle || '',
      city: driver.city || '',
      residenceLatitude: driver.residenceLatitude || '',
      residenceLongitude: driver.residenceLongitude || '',
      licenseNumber: driver.licenseNumber || '',
      licenseType: driver.licenseClass || '',
      status: driver.status || 'available',
      srcCardNumber: driver.srcCardNumber || '',
      srcExpiryDate: String(driver.src_expiry_date_text || driver.srcExpiryDate || driver.src_expiry_date || '').split('T')[0],
      joinDate: String(driver.start_date_text || driver.startDate || driver.start_date || driver.joinDate || '').split('T')[0],
      terminationDate: String(driver.termination_date_text || driver.terminationDate || driver.termination_date || '').split('T')[0],
      isControlResponsible: Boolean(driver.isControlResponsible),
      harcirahTuru: driver.harcirahTuru || 'sabit',
      workDuration: driver.workDuration || '',
      currentWeekDrivingHours: driver.currentWeekDrivingHours || 0,
      twoWeekDrivingHours: driver.twoWeekDrivingHours || 0,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (await showConfirm({ title: 'Sürücü Sil', message: 'Bu sürücüyü silmek istediğinizden emin misiniz?', confirmText: 'Sil', tone: 'warning' })) {
      try {
        const response = await driverService.deleteDriver(id);
        if (response.success) {
          toast.success('Sürücü silindi');
        } else {
          toast.error(response.message || 'Silme başarısız');
        }
        loadData();
      } catch (error) {
        console.error('Error deleting driver:', error);
        toast.error('Silme hatası');
      }
    }
  };

  const handleVehiclePlateChange = (value: string) => {
    const selectedVehicle = vehicleOptions.find((item: any) => {
      const plate = item.cekici_plaka || item.plate_number || item.plate || '';
      return plate === value;
    });

    setFormData((current) => ({
      ...current,
      cekiciPlaka: value,
      dorsePlaka: current.dorsePlaka || selectedVehicle?.linked_trailer_plate || selectedVehicle?.dorse_plaka || '',
      vehicleType: current.vehicleType || selectedVehicle?.linked_trailer_type || selectedVehicle?.vehicle_type || '',
    }));
  };

  const handleTrailerPlateChange = (value: string) => {
    const selectedTrailer = trailerOptions.find((item: any) => (item.plate || '') === value);
    setFormData((current) => ({
      ...current,
      dorsePlaka: value,
      vehicleType: selectedTrailer?.trailer_type || current.vehicleType || '',
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      tcNumber: '',
      cekiciPlaka: '',
      dorsePlaka: '',
      vehicleType: '',
      invoiceTitle: '',
      city: '',
      residenceLatitude: '',
      residenceLongitude: '',
      licenseNumber: '',
      licenseType: '',
      status: 'available',
      srcCardNumber: '',
      srcExpiryDate: '',
      joinDate: '',
      terminationDate: '',
      isControlResponsible: false,
      harcirahTuru: 'sabit',
      workDuration: '',
      currentWeekDrivingHours: 0,
      twoWeekDrivingHours: 0,
    });
    setEditingDriver(null);
    setModalTab('form');
    setDriverAuditLogs([]);
    setIsModalOpen(false);
  };

  const filteredDrivers = useMemo(() => drivers.filter(driver => {
    const terminated = Boolean(driver.terminationDate) || String(driver.userStatus || '').toLocaleLowerCase('tr-TR') === 'inactive';
    const matchesEmploymentTab = employmentTab === 'terminated' ? terminated : !terminated;
    const matchesSearch =
      (driver.fullName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (driver.phone || '').includes(searchTerm) ||
      (driver.tcNumber || '').includes(searchTerm) ||
      (driver.cekiciPlaka || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (driver.dorsePlaka || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLastLogin = lastLoginFilter === 'all'
      || (lastLoginFilter === 'has' && Boolean(driver.lastLogin))
      || (lastLoginFilter === 'none' && !driver.lastLogin);
    const matchesColumnFilters = Object.entries(columnFilters).every(([key, value]) => {
      if (!value) return true;
      const fieldValue = String((driver as any)[key] || '').toLocaleLowerCase('tr-TR');
      return fieldValue.includes(value.toLocaleLowerCase('tr-TR'));
    });
    return matchesEmploymentTab && matchesSearch && matchesLastLogin && matchesColumnFilters;
  }), [drivers, employmentTab, searchTerm, lastLoginFilter, columnFilters]);

  const employmentCounts = useMemo(() => {
    const terminated = drivers.filter((driver) => Boolean(driver.terminationDate) || String(driver.userStatus || '').toLocaleLowerCase('tr-TR') === 'inactive').length;
    return { active: Math.max(0, drivers.length - terminated), terminated };
  }, [drivers]);

  const sortedDrivers = useMemo(() => {
    if (!sortConfig) return filteredDrivers;
    return [...filteredDrivers].sort((a: any, b: any) => {
      const left = String(a?.[sortConfig.field] ?? '').toLocaleLowerCase('tr-TR');
      const right = String(b?.[sortConfig.field] ?? '').toLocaleLowerCase('tr-TR');
      if (left < right) return sortConfig.direction === 'asc' ? -1 : 1;
      if (left > right) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredDrivers, sortConfig]);

  const groupedDrivers = useMemo(() => {
    if (!groupBy) return { all: sortedDrivers };
    return sortedDrivers.reduce((acc: Record<string, any[]>, driver) => {
      const key = String((driver as any)[groupBy] || '-');
      if (!acc[key]) acc[key] = [];
      acc[key].push(driver);
      return acc;
    }, {});
  }, [sortedDrivers, groupBy]);

  const handleExportExcel = () => exportRowsToExcel(sortedDrivers, `drivers-${new Date().toISOString().slice(0, 10)}.xlsx`, {
    sheetName: 'Drivers',
    columns: tableColumns.map((column: any) => ({
      key: column.key,
      label: column.label,
      value: (driver: any) => {
        if (column.key === 'status') {
          return ({
            'available': 'Müsait',
            'on-route': 'Yolda',
            'resting': 'Dinlenme',
            'unavailable': 'Müsait Değil',
          } as Record<string, string>)[driver.status] || driver.status || '-';
        }
        if (column.key === 'isControlResponsible') {
          return driver.isControlResponsible ? 'Evet' : 'Hayır';
        }
        if (column.key === 'harcirahTuru') {
          return ({ 'sabit': 'Sabit Harcırah', 'aylik': 'Aylık Harcırah', 'dinamik': 'Dinamik Harcırah' } as Record<string, string>)[driver.harcirahTuru] || driver.harcirahTuru || '-';
        }
        if (column.key === 'assignedVehiclePlate') {
          return driver.assignedVehiclePlate || driver.assignedTrailerPlate || '-';
        }
        if (column.key === 'lastLogin') {
          return formatLastLogin(driver.lastLogin, driver.lastLoginPlatform);
        }
        if (column.key === 'personnelFiles') {
          return `${personnelDocumentCounts[String(driver.id)] || 0} evrak`;
        }
        if (column.key === 'terminationDate' && driver.terminationDate) {
          return driver.terminationDate;
        }
        return String(driver[column.key] || '-');
      },
    })),
  });

  const selectedDrivers = useMemo(
    () => drivers.filter((driver) => selectedDriverIds.includes(String(driver.id))),
    [drivers, selectedDriverIds],
  );

  const visibleDriverIds = useMemo(
    () => Object.values(groupedDrivers).flat().map((driver: any) => String(driver.id)),
    [groupedDrivers],
  );

  const allVisibleSelected = visibleDriverIds.length > 0 && visibleDriverIds.every((id) => selectedDriverIds.includes(id));

  const toggleVisibleDrivers = () => {
    setSelectedDriverIds((current) => allVisibleSelected
      ? current.filter((id) => !visibleDriverIds.includes(id))
      : Array.from(new Set([...current, ...visibleDriverIds])));
  };

  const toggleDriverSelection = (driverId: string) => {
    setSelectedDriverIds((current) => current.includes(driverId)
      ? current.filter((id) => id !== driverId)
      : [...current, driverId]);
  };

  const openBulkMessageModal = () => {
    if (selectedDrivers.length === 0) {
      toast.error('En az bir sürücü seçin');
      return;
    }
    setBulkMessageModalOpen(true);
  };

  const closeBulkMessageModal = () => {
    setBulkMessageModalOpen(false);
    setBulkMessageText('');
    setBulkMessageChannel('sms');
  };

  const handleBulkSendMessage = async () => {
    if (!bulkMessageText.trim()) {
      toast.error('Mesaj boş olamaz');
      return;
    }
    try {
      setBulkMessageSending(true);
      const response: any = await driverService.bulkSendMessage({
        driverIds: selectedDrivers.map((driver) => driver.id),
        message: bulkMessageText.trim(),
        channel: bulkMessageChannel,
        targetType: 'internal',
      });
      toast.success(response?.message || 'Toplu mesaj gönderildi');
      closeBulkMessageModal();
    } catch (error) {
      console.error('Bulk driver message send failed:', error);
      toast.error(error instanceof Error ? error.message : 'Toplu mesaj gönderilemedi');
    } finally {
      setBulkMessageSending(false);
    }
  };

  const handleSort = (field: string) => {
    setSortConfig((current) => {
      if (!current || current.field !== field) return { field, direction: 'asc' };
      if (current.direction === 'asc') return { field, direction: 'desc' };
      return null;
    });
  };

  const renderSortableHeader = (label: string, field: string) => (
    <button type="button" onClick={() => handleSort(field)} className="inline-flex items-center gap-1 font-semibold text-gray-700">
      <span>{label}</span>
      {sortConfig?.field === field ? (sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 text-blue-600" /> : <ArrowDown className="w-3 h-3 text-blue-600" />) : <ArrowUp className="w-3 h-3 text-gray-300" />}
    </button>
  );

  const tableColumns = useMemo(() => {
    const base = metadataColumns.length > 0
      ? metadataColumns.filter((column) => column.visibleByDefault !== false)
      : [
          { key: 'fullName', label: 'Sürücü Adı' },
          { key: 'phone', label: 'Telefon' },
          { key: 'tcNumber', label: 'TC Kimlik' },
          { key: 'cekiciPlaka', label: 'Çekici' },
          { key: 'dorsePlaka', label: 'Dorse' },
          { key: 'vehicleType', label: 'Dorse Tipi' },
          { key: 'invoiceTitle', label: 'Fatura Başlığı' },
          { key: 'city', label: 'İkamet' },
          { key: 'isControlResponsible', label: 'Kontrol Sorumlusu' },
          { key: 'harcirahTuru', label: 'Harcırah Türü' },
          { key: 'assignedVehiclePlate', label: 'Aktif Araç' },
          { key: 'personnelFiles', label: 'Özlük' },
          { key: 'lastLogin', label: 'Son Giriş' },
          { key: 'status', label: 'Durum' },
        ];
    const withResponsible = base.some((column: any) => column.key === 'isControlResponsible') ? base : [...base, { key: 'isControlResponsible', label: 'Kontrol Sorumlusu' }];
    const withPersonnel = withResponsible.some((column: any) => column.key === 'personnelFiles') ? withResponsible : [...withResponsible, { key: 'personnelFiles', label: 'Özlük' }];
    return withPersonnel.some((column: any) => column.key === 'lastLogin') ? withPersonnel : [...withPersonnel, { key: 'lastLogin', label: 'Son Giriş' }];
  }, [metadataColumns]);

  const getStatusBadge = (status: string) => {
    const styles = {
      'available': 'bg-green-100 text-green-800',
      'on-route': 'bg-blue-100 text-blue-800',
      'resting': 'bg-orange-100 text-orange-800',
      'unavailable': 'bg-red-100 text-red-800',
    };
    const labels = {
      'available': 'Müsait',
      'on-route': 'Yolda',
      'resting': 'Dinlenme',
      'unavailable': 'Müsait Değil',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status as keyof typeof styles]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  const formatLastLogin = (value?: string, platform?: string) => {
    if (!value) return '-';
    const normalized = String(value).replace('T', ' ').slice(0, 19);
    const date = new Date(normalized.replace(' ', 'T'));
    const formatted = Number.isNaN(date.getTime())
      ? normalized
      : date.toLocaleString('tr-TR');
    return platform ? `${formatted} (${platform})` : formatted;
  };

  const syncInternalDrivers = async () => {
    try {
      setSyncingWorkiom(true);
      const response = await driverService.syncInternalDriversFromWorkiom();
      toast.success(response.message || 'Workiom özmal sürücüleri güncellendi');
      await loadData();
    } catch (error) {
      console.error('Error syncing internal drivers:', error);
      toast.error(error instanceof Error ? error.message : 'Workiom sürücü senkronizasyonu başarısız');
    } finally {
      setSyncingWorkiom(false);
    }
  };

  const syncInternalVehicleInfo = async () => {
    try {
      setSyncingVehicleInfo(true);
      const response: any = await driverService.syncInternalDriverVehicleInfo();
      toast.success(response.message || 'Özmal sürücü araç bilgileri güncellendi');
      await loadData();
    } catch (error) {
      console.error('Error syncing internal vehicle info:', error);
      toast.error('Özmal sürücü araç bilgileri güncellenemedi');
    } finally {
      setSyncingVehicleInfo(false);
    }
  };

  const syncInternalDriversFromSeyir = async () => {
    try {
      setSyncingSeyir(true);
      const response: any = await driverService.syncInternalDriversFromSeyir();
      toast.success(response.message || 'Seyir verileri güncellendi');
      await loadData();
    } catch (error) {
      console.error('Error syncing internal drivers from Seyir:', error);
      toast.error(error instanceof Error ? error.message : 'Seyir verileri güncellenemedi');
    } finally {
      setSyncingSeyir(false);
    }
  };

  const fillInternalResidenceCoordinates = async () => {
    try {
      setFillingResidenceCoordinates(true);
      const response: any = await driverService.fillResidenceCoordinates();
      toast.success(response?.message || 'İkamet koordinatları dolduruldu');
      await loadData();
    } catch (error) {
      console.error('Error filling residence coordinates:', error);
      toast.error(error instanceof Error ? error.message : 'İkamet koordinatları doldurulamadı');
    } finally {
      setFillingResidenceCoordinates(false);
    }
  };

  const resolvedDriverFields = useMemo(() => {
    const base = metadataFields.length > 0 ? metadataFields.map((field) => field.key) : ['name', 'email', 'phone', 'tcNumber', 'cekiciPlaka', 'dorsePlaka', 'vehicleType', 'invoiceTitle', 'city', 'licenseNumber', 'licenseType', 'status', 'joinDate', 'terminationDate', 'srcCardNumber', 'srcExpiryDate', 'workDuration', 'currentWeekDrivingHours', 'twoWeekDrivingHours'];
    const withResponsible = base.includes('isControlResponsible') ? base : [...base, 'isControlResponsible'];
    const withHarcirah = withResponsible.includes('harcirahTuru') ? withResponsible : [...withResponsible, 'harcirahTuru'];
    const withResidenceLatitude = withHarcirah.includes('residenceLatitude') ? withHarcirah : [...withHarcirah, 'residenceLatitude'];
    return withResidenceLatitude.includes('residenceLongitude') ? withResidenceLatitude : [...withResidenceLatitude, 'residenceLongitude'];
  }, [metadataFields]);

  const renderDriverField = (fieldKey: string) => {
    switch (fieldKey) {
      case 'name': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad *</label><input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: normalizeDriverName(e.target.value)})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'email': return <div><label className="block text-sm font-medium text-gray-700 mb-1">E-posta</label><input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'phone': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Telefon *</label><input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="05XX XXX XX XX" /></div>;
      case 'tcNumber': return <div><label className="block text-sm font-medium text-gray-700 mb-1">TC Kimlik No</label><input type="text" value={formData.tcNumber} onChange={(e) => setFormData({...formData, tcNumber: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'cekiciPlaka': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Çekici</label><input list="vehicle-plate-options" type="text" value={formData.cekiciPlaka} onChange={(e) => handleVehiclePlateChange(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Sistemden çekici seçin veya arayın" /><datalist id="vehicle-plate-options">{vehicleOptions.map((item: any) => { const plate = item.cekici_plaka || item.plate_number || item.plate || ''; return <option key={item.id} value={plate}>{plate}</option>; })}</datalist></div>;
      case 'dorsePlaka': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Dorse</label><input list="trailer-plate-options" type="text" value={formData.dorsePlaka} onChange={(e) => handleTrailerPlateChange(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Sistemden dorse seçin veya arayın" /><datalist id="trailer-plate-options">{trailerOptions.map((item: any) => <option key={item.id} value={item.plate || ''}>{item.plate || ''}</option>)}</datalist></div>;
      case 'vehicleType': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Dorse Tipi</label><select value={formData.vehicleType} onChange={(e) => setFormData({...formData, vehicleType: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg"><option value="">Seçiniz</option>{trailerTypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></div>;
      case 'invoiceTitle': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Fatura Başlığı</label><input type="text" value={formData.invoiceTitle} onChange={(e) => setFormData({...formData, invoiceTitle: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'city': return <div><label className="block text-sm font-medium text-gray-700 mb-1">İkamet</label><input type="text" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'residenceLatitude': return <div><label className="block text-sm font-medium text-gray-700 mb-1">İkamet Enlem</label><input type="number" step="0.00000001" value={formData.residenceLatitude} onChange={(e) => setFormData({...formData, residenceLatitude: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'residenceLongitude': return <div><label className="block text-sm font-medium text-gray-700 mb-1">İkamet Boylam</label><input type="number" step="0.00000001" value={formData.residenceLongitude} onChange={(e) => setFormData({...formData, residenceLongitude: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'licenseNumber': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Ehliyet Numarası *</label><input type="text" required value={formData.licenseNumber} onChange={(e) => setFormData({...formData, licenseNumber: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'licenseType': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Ehliyet Sınıfı *</label><select required value={formData.licenseType} onChange={(e) => setFormData({...formData, licenseType: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg"><option value="">Seçiniz</option><option value="B">B</option><option value="C">C</option><option value="C+E">C+E</option><option value="D">D</option><option value="E">E</option></select></div>;
      case 'status': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Durum *</label><select required value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value as any})} className="w-full px-3 py-2 border border-gray-300 rounded-lg"><option value="available">Müsait</option><option value="on-route">Yolda</option><option value="resting">Dinlenme</option><option value="unavailable">Müsait Değil</option></select></div>;
      case 'joinDate': return <div><label className="block text-sm font-medium text-gray-700 mb-1">İşe Başlama Tarihi *</label><input type="date" required value={formData.joinDate} onChange={(e) => setFormData({...formData, joinDate: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'terminationDate': return <div><label className="block text-sm font-medium text-gray-700 mb-1">İşten Çıkış Tarihi</label><input type="date" value={formData.terminationDate} onChange={(e) => setFormData({...formData, terminationDate: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'isControlResponsible': return <div><label className="mb-1 block text-sm font-medium text-gray-700">Kontrol Sorumlusu</label><label className="inline-flex items-center gap-3 rounded-lg border border-gray-300 px-3 py-2"><input type="checkbox" checked={formData.isControlResponsible} onChange={(e) => setFormData({ ...formData, isControlResponsible: e.target.checked })} className="h-4 w-4 rounded border-gray-300" /><span className="text-sm text-gray-700">Araç kontrol formlarında kontrol eden olarak seçilebilsin</span></label></div>;
      case 'harcirahTuru': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Harcırah Türü</label><select value={formData.harcirahTuru} onChange={(e) => setFormData({...formData, harcirahTuru: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg"><option value="sabit">Sabit Harcırah</option><option value="aylik">Aylık Harcırah</option><option value="dinamik">Dinamik Harcırah</option></select></div>;
      case 'srcCardNumber': return <div><label className="block text-sm font-medium text-gray-700 mb-1">SRC Kart Numarası</label><input type="text" value={formData.srcCardNumber} onChange={(e) => setFormData({...formData, srcCardNumber: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'srcExpiryDate': return <div><label className="block text-sm font-medium text-gray-700 mb-1">SRC Bitiş Tarihi</label><input type="date" value={formData.srcExpiryDate} onChange={(e) => setFormData({...formData, srcExpiryDate: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'workDuration': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Çalışma Süresi</label><input type="text" value={formData.workDuration} onChange={(e) => setFormData({...formData, workDuration: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'currentWeekDrivingHours': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Bu Hafta Çalışma Saati</label><input type="text" value={formData.currentWeekDrivingHours} onChange={(e) => setFormData({...formData, currentWeekDrivingHours: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      case 'twoWeekDrivingHours': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Son İki Hafta Çalışma Saati</label><input type="text" value={formData.twoWeekDrivingHours} onChange={(e) => setFormData({...formData, twoWeekDrivingHours: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>;
      default: return null;
    }
  };

  const isAdminUser = useMemo(() => {
    const effectiveRoles = Array.from(new Set([currentUser?.role, ...(currentUser?.roles || [])].filter(Boolean)));
    return effectiveRoles.includes('admin');
  }, [currentUser?.role, currentUser?.roles]);

  const handleImpersonateDriver = async (driver: any) => {
    const targetUserId = String(driver.user_id || driver.userId || '');
    if (!targetUserId) {
      toast.error('Bu sürücü için kullanıcı hesabı bulunamadı');
      return;
    }

    if (!(await showConfirm({
      title: 'Kullanıcı Olarak Giriş Yap',
      message: `${driver.fullName || driver.name || 'Sürücü'} hesabına geçilsin mi?`,
      confirmText: 'Giriş Yap',
      tone: 'info',
    }))) {
      return;
    }

    const response = await impersonateUser(targetUserId);
    if (!response.success) {
      toast.error(response.message || 'Kullanıcı oturumu açılamadı');
      return;
    }

    toast.success(`${driver.fullName || driver.name || 'Sürücü'} hesabına geçildi`);
    const effectiveRoles = Array.from(new Set([response.user?.role, ...(response.user?.roles || [])].filter(Boolean)));
    navigate(effectiveRoles.includes('internal-driver') || effectiveRoles.includes('external-driver') ? '/driver-mobile-home' : '/');
  };

  const handleGenerateVerificationCode = async (driver: any) => {
    const userId = String(driver.user_id || driver.userId || '');
    if (!userId) {
      toast.error('Bu sürücü için kullanıcı hesabı bulunamadı');
      return;
    }
    try {
      const response: any = await authService.generateAdminVerificationCode(userId);
      const data = response?.data;
      if (!data?.code || !data?.phone) throw new Error(response?.message || 'Doğrulama kodu oluşturulamadı');
      const message = `Yük Ortağım doğrulama kodunuz: ${data.code}. Kod 5 dakika geçerlidir.`;
      await navigator.clipboard?.writeText(message).catch(() => {});
      window.open(`https://web.whatsapp.com/send?phone=90${data.phone}&text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
      toast.success(`Kod oluşturuldu: ${data.code}. Mesaj WhatsApp için panoya kopyalandı.`);
    } catch (error: any) {
      toast.error(error?.message || 'Doğrulama kodu oluşturulamadı');
    }
  };

  return (
    <div className="workiom-screen">
      <div className="workiom-toolbar">
        <div className="workiom-toolbar-left">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sürücü Yönetimi</h1>
          <p className="text-gray-500 mt-1">Özmal sürücüleri yönetin</p>
        </div>
        </div>
        <div className="workiom-toolbar-right">
            <button type="button" onClick={handleExportExcel} className="workiom-outline-button">
              Excele Aktar
            </button>
            <button type="button" onClick={openBulkMessageModal} className="workiom-outline-button" disabled={selectedDriverIds.length === 0}>
              <MessageCircle className="w-4 h-4" /> Toplu WhatsApp / SMS Gönder ({selectedDriverIds.length})
            </button>
            <button
              onClick={syncInternalDrivers}
              disabled={syncingWorkiom}
              className="workiom-soft-button disabled:opacity-50"
            >
              {syncingWorkiom ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Workiom'dan Güncelle
            </button>
            <button
              onClick={syncInternalVehicleInfo}
              disabled={syncingVehicleInfo}
              className="workiom-soft-button disabled:opacity-50"
            >
              {syncingVehicleInfo ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Çekici ve Dorseleri Güncelle
            </button>
            <button
              onClick={fillInternalResidenceCoordinates}
              disabled={fillingResidenceCoordinates}
              className="workiom-soft-button disabled:opacity-50"
            >
              {fillingResidenceCoordinates ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              İkamet Koordinatlarını Doldur
            </button>
            <button
              onClick={syncInternalDriversFromSeyir}
              disabled={syncingSeyir}
              className="workiom-soft-button disabled:opacity-50"
            >
              {syncingSeyir ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Seyir Verilerini Güncelle
            </button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="workiom-primary-button"
            >
              <Plus className="w-5 h-5" />
              Yeni Sürücü
            </button>
        </div>
      </div>

      <div className="workiom-toolbar !justify-start">
        <div className="flex flex-wrap items-center gap-3 w-full">
          <div className="flex rounded-xl bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => setEmploymentTab('active')}
              className={`rounded-lg px-4 py-2 text-sm font-bold transition ${employmentTab === 'active' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Çalışan ({employmentCounts.active})
            </button>
            <button
              type="button"
              onClick={() => setEmploymentTab('terminated')}
              className={`rounded-lg px-4 py-2 text-sm font-bold transition ${employmentTab === 'terminated' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              İşten Ayrılan ({employmentCounts.terminated})
            </button>
          </div>
        <button
          type="button"
          onClick={() => setShowMobileTools((current) => !current)}
          className={`workiom-outline-button md:hidden ${showMobileTools ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}
        >
          <Search className="w-4 h-4" /> Ara / Filtre
        </button>
          <div className={`${showMobileTools ? 'flex' : 'hidden'} workiom-search max-w-md md:flex`}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="İsim, telefon, TC, plaka ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="outline-none"
          />
        </div>
          <div className={`${showMobileTools ? 'flex' : 'hidden'} w-full flex-wrap items-center gap-2 md:flex md:w-auto`}>
          <select value={lastLoginFilter} onChange={(e) => setLastLoginFilter(e.target.value as 'all' | 'has' | 'none')} className="rounded-lg border border-gray-300 px-4 py-2">
            <option value="all">Son Giriş: Tümü</option>
            <option value="has">Son Giriş: Olanlar</option>
            <option value="none">Son Giriş: Olmayanlar</option>
          </select>
          <button onClick={() => setShowColumnFilters((prev) => !prev)} className={`workiom-outline-button ${showColumnFilters ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}>
            <Filter className="w-4 h-4" /> Filtrele
          </button>
          <button onClick={() => setShowGrouping((prev) => !prev)} className={`workiom-outline-button ${showGrouping ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}>
            <Layers className="w-4 h-4" /> Gruplandır
          </button>
          {showGrouping && (
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg">
              <option value="">Gruplama Yok</option>
              <option value="status">Durum</option>
              <option value="city">İkamet</option>
              <option value="vehicleType">Dorse Tipi</option>
              <option value="invoiceTitle">Fatura Başlığı</option>
            </select>
          )}
        </div>
        </div>
      </div>

      <div className="workiom-grid-wrap overflow-auto" style={{ height: '450px', maxHeight: '450px' }}>
        <table className="workiom-grid-table text-sm">
          <thead>
            <tr>
              <th className="workiom-grid-index text-left">#</th>
              <th className="text-left"><input type="checkbox" checked={allVisibleSelected} onChange={toggleVisibleDrivers} /></th>
              {tableColumns.map((column, index) => (
                <th key={column.key} className={index === 0 ? 'workiom-grid-sticky-col text-left' : 'text-left'}>{renderSortableHeader(column.label, column.key)}</th>
              ))}
              <th className="text-left">İşlemler</th>
            </tr>
            {showColumnFilters && (
              <tr>
                <th className="workiom-grid-index"></th>
                <th></th>
                {tableColumns.map((column) => (
                  <th key={`filter-${column.key}`}><input value={columnFilters[column.key] || ''} onChange={(e) => setColumnFilters((prev) => ({ ...prev, [column.key]: e.target.value }))} placeholder={`${column.label} filtre`} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" /></th>
                ))}
                <th></th>
              </tr>
            )}
          </thead>
          <tbody>
            {Object.entries(groupedDrivers).flatMap(([groupKey, groupDrivers]) => {
              const rows: any[] = [];
              if (groupBy) {
                rows.push(
                  <tr key={`group-${groupKey}`} className="bg-gray-100">
                    <td colSpan={tableColumns.length + 3} className="px-3 py-2">
                      <button type="button" onClick={() => setCollapsedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }))} className="flex items-center gap-2 text-sm font-medium text-gray-900">
                        {collapsedGroups[groupKey] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        <span>{groupKey}</span>
                        <span className="text-gray-500">({groupDrivers.length})</span>
                      </button>
                    </td>
                  </tr>
                );
              }
              if (!collapsedGroups[groupKey]) {
                rows.push(...groupDrivers.map((driver, index) => (
              <tr key={driver.id} onDoubleClick={() => handleEdit(driver)} className="cursor-pointer">
                <td className="workiom-grid-index">{index + 1}</td>
                <td><input type="checkbox" checked={selectedDriverIds.includes(String(driver.id))} onChange={() => toggleDriverSelection(String(driver.id))} /></td>
                {tableColumns.map((column, columnIndex) => (
                  <td key={column.key} className={columnIndex === 0 ? 'workiom-grid-sticky-col font-medium text-gray-900' : ''}>
                    {column.key === 'status'
                      ? getStatusBadge(driver.status)
                      : column.key === 'isControlResponsible'
                        ? (driver.isControlResponsible ? 'Evet' : 'Hayır')
                      : column.key === 'harcirahTuru'
                        ? ({ 'sabit': 'Sabit Harcırah', 'aylik': 'Aylık Harcırah', 'dinamik': 'Dinamik Harcırah' } as Record<string, string>)[driver.harcirahTuru] || driver.harcirahTuru || '-'
                      : column.key === 'assignedVehiclePlate'
                        ? (driver.assignedVehiclePlate || driver.assignedTrailerPlate ? <Link to={`/vehicles?vehicleId=${driver.assignedVehicleId || ''}`} className="text-blue-600 hover:underline">{driver.assignedVehiclePlate || driver.assignedTrailerPlate}</Link> : '-')
                      : column.key === 'cekiciPlaka'
                        ? ((driver.cekiciPlaka || '').trim()
                          ? <Link to={`/vehicle-documents?tab=tractor${driver.assignedVehicleId ? `&vehicleId=${encodeURIComponent(String(driver.assignedVehicleId))}` : `&plate=${encodeURIComponent(String(driver.cekiciPlaka || ''))}`}`} className="text-blue-600 hover:underline">{driver.cekiciPlaka}</Link>
                          : '-')
                      : column.key === 'dorsePlaka'
                        ? ((driver.dorsePlaka || '').trim()
                          ? <Link to={`/vehicle-documents?tab=trailer&plate=${encodeURIComponent(String(driver.dorsePlaka || ''))}`} className="text-blue-600 hover:underline">{driver.dorsePlaka}</Link>
                          : '-')
                      : column.key === 'lastLogin'
                          ? formatLastLogin(driver.lastLogin, driver.lastLoginPlatform)
                      : column.key === 'personnelFiles'
                          ? (
                            <Link
                              to={`/personnel-files?driverId=${encodeURIComponent(String(driver.id))}`}
                              className="inline-flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-violet-700 hover:bg-violet-100"
                            >
                              <FolderOpen className="h-4 w-4" />
                              <span className="text-xs font-medium">{personnelDocumentCounts[String(driver.id)] || 0} evrak</span>
                            </Link>
                          )
                        : column.key === 'terminationDate' && driver.terminationDate
                          ? <span className="inline-flex rounded-full bg-rose-50 px-2 py-1 text-xs font-bold text-rose-700">{driver.terminationDate}</span>
                        : String((driver as any)[column.key] || '-')}
                  </td>
                ))}
                <td>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleEdit(driver)} className="inline-flex items-center gap-1 px-3 py-1.5 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100">
                      <Edit className="w-4 h-4" />
                      Düzenle
                    </button>
                    <button onClick={() => handleDelete(driver.id)} className="inline-flex items-center gap-1 px-3 py-1.5 text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                      <Trash2 className="w-4 h-4" />
                      Sil
                    </button>
                    {String(driver.user_id || driver.userId || '') && (
                      <button onClick={() => void handleGenerateVerificationCode(driver)} className="inline-flex items-center gap-1 px-3 py-1.5 text-violet-700 bg-violet-50 rounded-lg hover:bg-violet-100" title="5 dakika geçerli kod oluşturur ve WhatsApp Web'i açar">
                        <KeyRound className="w-4 h-4" />
                        Doğrulama Kodu Oluştur
                      </button>
                    )}
                    {isAdminUser && String(driver.user_id || driver.userId || '') && String(driver.user_id || driver.userId || '') !== String(currentUser?.id || '') && (
                      <button onClick={() => void handleImpersonateDriver(driver)} className="inline-flex items-center gap-1 px-3 py-1.5 text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100">
                        <LogIn className="w-4 h-4" />
                        Bu Kullanıcı Olarak Giriş Yap
                      </button>
                    )}
                  </div>
                </td>
              </tr>
                )));
              }
              return rows;
            })}
          </tbody>
        </table>

        {!loading && filteredDrivers.length === 0 && (
          <div className="py-16 text-center text-gray-500">Sürücü bulunamadı</div>
        )}
      </div>
      <div className="mb-6 flex items-center justify-end gap-3">
        <div className="px-4 py-3 text-sm text-gray-500">Toplam {sortedDrivers.length} kayıt gösteriliyor</div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="flex max-h-[calc(100vh-1rem)] w-full max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-lg bg-white sm:max-h-[90vh] sm:max-w-2xl">
            <div className="border-b border-gray-200 p-4 sm:p-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingDriver ? 'Sürücüyü Düzenle' : 'Yeni Sürücü Ekle'}
              </h2>
            </div>
            {editingDriver && (
              <div className="flex gap-1 border-b border-gray-200 px-4 sm:px-6">
                <button
                  type="button"
                  onClick={() => setModalTab('form')}
                  className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${modalTab === 'form' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                  Sürücü Bilgileri
                </button>
                <button
                  type="button"
                  onClick={() => { setModalTab('kunya'); loadDriverAuditLogs(editingDriver.id); }}
                  className={`-mb-px flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${modalTab === 'kunya' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                  <History className="h-4 w-4" /> Kayıt Künyesi
                </button>
              </div>
            )}
            {modalTab === 'form' ? (
              <form onSubmit={handleSubmit} className="min-h-0 flex-1 overflow-y-auto p-4 space-y-4 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {resolvedDriverFields.map((fieldKey) => (
                    <div key={fieldKey}>{renderDriverField(fieldKey)}</div>
                  ))}
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingDriver ? 'Güncelle' : 'Ekle'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-3 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    {driverAuditLogs.length > 0 ? `${driverAuditLogs.length} işlem kaydı` : 'Kayıt bulunmuyor'}
                  </div>
                  <button
                    type="button"
                    onClick={() => editingDriver && loadDriverAuditLogs(editingDriver.id)}
                    className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${driverAuditLogsLoading ? 'animate-spin' : ''}`} /> Yenile
                  </button>
                </div>
                {driverAuditLogsLoading ? (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4 text-sm text-gray-500">İşlem kayıtları yükleniyor...</div>
                ) : driverAuditLogs.length === 0 ? (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4 text-sm text-gray-500">Bu sürücü için işlem kaydı bulunmuyor</div>
                ) : (
                  driverAuditLogs.map((log) => (
                    <div key={String(log.id)} className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-xs text-gray-500">{formatLogDateTime(log.logged_at)}</div>
                          <div className="mt-1 text-sm font-semibold text-gray-900">{log.description || `${log.category || '-'} / ${log.action || '-'}`}</div>
                          <div className="mt-1 text-xs text-gray-600">Yapan: {log.user_name || 'Sistem'}{log.user_role ? ` • Rol: ${log.user_role}` : ''}</div>
                          <div className="mt-1 text-xs text-gray-400">Kategori: {log.category || '-'} • İşlem: {log.action || '-'}</div>
                          {log.error_message ? <div className="mt-1 text-xs text-rose-600">Hata: {log.error_message}</div> : null}
                        </div>
                        <div className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold ${log.success ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                          {log.success ? 'Başarılı' : 'Hata'}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {bulkMessageModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900">Toplu WhatsApp / SMS Gönder</h2>
              <p className="mt-1 text-sm text-gray-500">Seçili {selectedDrivers.length} özmal sürücüye mesaj gönderin.</p>
            </div>
            <div className="space-y-4 p-6">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Kanal</label>
                <select value={bulkMessageChannel} onChange={(e) => setBulkMessageChannel(e.target.value as 'sms' | 'whatsapp')} className="w-full rounded-lg border border-gray-300 px-3 py-2">
                  <option value="sms">SMS</option>
                  <option value="whatsapp">WhatsApp (QR Kod)</option>
                </select>
              </div>
              {bulkMessageChannel === 'whatsapp' && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                  <div className="font-medium">WhatsApp Web ile gönderim</div>
                  <div className="mt-1">Bu gönderim QR kod ile bağlanan WhatsApp Web oturumu üzerinden yapılır.</div>
                  <button type="button" onClick={() => window.open('/whatsapp-web', '_blank', 'noopener,noreferrer')} className="mt-3 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-emerald-700 hover:bg-emerald-100">
                    WhatsApp Web / QR Kod Ekranını Aç
                  </button>
                </div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Mesaj</label>
                <textarea value={bulkMessageText} onChange={(e) => setBulkMessageText(e.target.value)} rows={6} className="w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="Gönderilecek mesajı yazın" />
              </div>
              <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                {selectedDrivers.map((driver) => (
                  <div key={driver.id}>{driver.fullName || driver.name || '-'} - {driver.phone || '-'}</div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 p-6">
              <button type="button" onClick={closeBulkMessageModal} className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200">İptal</button>
              <button type="button" onClick={handleBulkSendMessage} disabled={bulkMessageSending} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50">
                <Send className="h-4 w-4" /> {bulkMessageSending ? 'Gönderiliyor...' : 'Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {personnelModalDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="flex max-h-[calc(100vh-1rem)] w-full max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-lg bg-white sm:max-h-[90vh] sm:max-w-4xl">
            <div className="flex items-center justify-between border-b border-gray-200 p-4 sm:p-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Özlük Dosyaları</h2>
                <p className="text-sm text-gray-500 mt-1">{personnelModalDriver.fullName || personnelModalDriver.name} için PDF evrakları yükleyin</p>
              </div>
              <button type="button" onClick={closePersonnelModal} className="px-3 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Kapat</button>
            </div>

            <div className="p-6 space-y-6">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="grid grid-cols-1 gap-4 items-end md:grid-cols-[220px_180px_minmax(0,1fr)_220px]">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Evrak Türü</label>
                    <select value={personnelDocumentForm.documentType} onChange={(e) => setPersonnelDocumentForm((current) => ({ ...current, documentType: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                      <option value="">Seçiniz</option>
                      {personnelDocumentTypes.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Evrak Tarihi</label>
                    <input type="date" value={personnelDocumentForm.documentDate} onChange={(e) => setPersonnelDocumentForm((current) => ({ ...current, documentDate: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Not</label>
                    <input type="text" value={personnelDocumentForm.notes} onChange={(e) => setPersonnelDocumentForm((current) => ({ ...current, notes: e.target.value }))} placeholder="İsteğe bağlı not" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div>
                    <label className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-white hover:bg-violet-700">
                      <Upload className="w-4 h-4" />
                      {personnelDocumentUploading ? 'Yükleniyor...' : 'PDF Yükle'}
                      <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={handleUploadPersonnelDocument} disabled={personnelDocumentUploading} />
                    </label>
                  </div>
                </div>
                <p className="mt-3 text-xs text-gray-500">Evrak türleri Ayarlar &gt; Statik Liste Tanımları altında <code>internal_driver_personnel_document_types</code> anahtarından yönetilir.</p>
              </div>

              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Tür</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Evrak Tarihi</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Dosya</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Not</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Yüklenme</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {personnelDocumentsLoading ? (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Yükleniyor...</td></tr>
                    ) : personnelDocuments.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Henüz özlük dosyası yok</td></tr>
                    ) : personnelDocuments.map((doc) => (
                      <tr key={doc.id} className="border-t border-slate-100">
                        <td className="px-4 py-3 text-slate-800">{doc.document_type || '-'}</td>
                        <td className="px-4 py-3 text-slate-600">{doc.document_date ? new Date(doc.document_date).toLocaleDateString('tr-TR') : '-'}</td>
                        <td className="px-4 py-3 text-slate-800">{doc.file_name || 'Belge'}</td>
                        <td className="px-4 py-3 text-slate-600">{doc.notes || '-'}</td>
                        <td className="px-4 py-3 text-slate-600">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString('tr-TR') : '-'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => window.open(resolveFileUrl(doc.file_path || doc.file_url || ''), '_blank', 'noopener,noreferrer')} className="inline-flex items-center gap-1 px-3 py-1.5 text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100">
                              <FileText className="w-4 h-4" />
                              Görüntüle
                            </button>
                            <button type="button" onClick={() => void handleDeletePersonnelDocument(doc.id)} className="inline-flex items-center gap-1 px-3 py-1.5 text-red-700 bg-red-50 rounded-lg hover:bg-red-100">
                              <Trash2 className="w-4 h-4" />
                              Sil
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
